import { useEffect } from "react";
import { Sparkles, RotateCcw, RefreshCw, Home } from "lucide-react";
import confetti from "canvas-confetti";
import type { GameType, SoundStyle } from "@juegossc/shared";
import { Modal } from "./Modal";
import { playEvent } from "../../lib/audio";

// Confetti corto cuando se gana premio real — celebra sin saturar.
// Duración total ~1.5s. Si querés más fiesta, subir DURATION_MS y
// los particleCount; si menos, bajar.
function celebrateWin() {
  const DURATION_MS = 1500;
  const end = Date.now() + DURATION_MS;
  const colors = ["#f7c948", "#ff6b6b", "#a78bfa", "#00c9b1", "#fff"];

  // Burst inicial central
  confetti({
    particleCount: 60,
    spread: 80,
    origin: { y: 0.5 },
    colors,
  });

  // Lluvia continua desde laterales (corta)
  (function frame() {
    confetti({
      particleCount: 2,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 },
      colors,
    });
    confetti({
      particleCount: 2,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 },
      colors,
    });

    if (Date.now() < end) requestAnimationFrame(frame);
  })();

  // Pelotas (un burst chico)
  window.setTimeout(() => {
    confetti({
      particleCount: 8,
      spread: 70,
      origin: { y: 0.4 },
      shapes: ["circle"],
      scalar: 1.4,
    });
  }, 200);
}

// Cue positivo CHICO para el "intento extra": un solo burst verde, sin la
// lluvia continua de celebrateWin. Suficiente para que el cliente que NO lee
// igual perciba que ganó algo bueno (una jugada extra), sin igualar la fiesta
// del premio real (pedido del user: intento extra debe leerse "ganaste algo").
function celebrateRetry() {
  confetti({
    particleCount: 36,
    spread: 70,
    origin: { y: 0.5 },
    colors: ["#34d399", "#6ee7b7", "#a7f3d0", "#fff"],
  });
}

type Props = {
  open: boolean;
  premioNombre: string;
  isConsolation: boolean;
  /** Migration 014: reintento = el cliente ganó un intento extra
   *  (modal distinto al de premio real y al de consolation, con CTA
   *  "Jugar otra vez" en lugar de "Volver a jugar"). */
  isReintento?: boolean;
  onNext: () => void;
  /** Volver a la pantalla principal (landing). Si está definido y
   *  NO es reintento, el modal muestra un segundo botón para que el
   *  cliente decida si sigue jugando o vuelve al menú. En reintento
   *  se ignora — el cliente tiene que usar su intento extra. */
  onMainMenu?: () => void;
  /** Estilo de sonido del shopping (clásico, casino, festivo, silent). */
  soundStyle?: SoundStyle;
  /** Juego que abre el modal — define qué slot de win/consolation se
   *  dispara (raspadita-win es distinto de ruleta-win en el refactor
   *  de eventos 2026-06-02). Default 'ruleta' para back-compat. */
  gameType?: GameType;
  /** Auto-cerrar tras N segundos (default 120s). 0 deshabilita. */
  autoCloseSeconds?: number;
};

export function PremioModal({
  open,
  premioNombre,
  isConsolation,
  isReintento = false,
  onNext,
  onMainMenu,
  soundStyle = "classic",
  gameType = "ruleta",
  autoCloseSeconds = 120,
}: Props) {
  // Sonido + confetti al abrir. Reintento usa el slot 'win' del juego
  // (semánticamente "ganaste algo", aunque sea una jugada extra), sin
  // confetti exagerado.
  useEffect(() => {
    if (!open) return;
    if (isReintento) {
      playEvent(gameType, "win", soundStyle);
      celebrateRetry();
    } else if (isConsolation) {
      playEvent(gameType, "consolation", soundStyle);
    } else {
      playEvent(gameType, "win", soundStyle);
      celebrateWin();
    }
  }, [open, isConsolation, isReintento, soundStyle, gameType]);

  // Auto-reset por seguridad
  useEffect(() => {
    if (!open || autoCloseSeconds <= 0) return;
    const t = window.setTimeout(onNext, autoCloseSeconds * 1000);
    return () => window.clearTimeout(t);
  }, [open, onNext, autoCloseSeconds]);

  return (
    <Modal open={open}>
      <div className="flex flex-col items-center gap-6 text-center">
        {isReintento ? (
          // Reintento: el cliente ganó otra jugada gratis. Paleta VERDE
          // (no azul/neutro) para que se lea como algo bueno que ganó.
          <>
            <RefreshCw className="h-20 w-20 text-emerald-400" />
            <h2 className="font-heading text-2xl font-bold">¡Intento extra!</h2>
            <p className="font-body text-lg opacity-80">
              Te ganaste una jugada más. ¡Probá de nuevo!
            </p>
          </>
        ) : isConsolation ? (
          // "Casi": tono mas frio/calmo (trebol desaturado + opacidad) para
          // que se lea como "no ganaste esta vez", pero sin agresividad
          // (nada de rojo). Energia visual por debajo del premio real.
          <>
            <div className="text-7xl opacity-80 saturate-50">🍀</div>
            <h2 className="font-heading text-3xl font-bold">¡Casi!</h2>
            <p className="font-body text-lg opacity-80">
              Seguí comprando para seguir participando.
            </p>
            <p className="font-body text-sm opacity-60">
              La próxima podés ganarte algo grande.
            </p>
          </>
        ) : (
          <>
            <Sparkles className="h-20 w-20 text-yellow-400" />
            <h2 className="font-heading text-2xl font-bold">¡Felicitaciones!</h2>
            <div>
              <p className="font-body mb-2 text-lg opacity-80">Te ganaste:</p>
              <h1 className="font-display bg-gradient-to-r from-yellow-300 via-orange-400 to-red-400 bg-clip-text text-5xl font-bold text-transparent leading-tight">
                {premioNombre}
              </h1>
            </div>
          </>
        )}

        {/* Acciones — reintento solo tiene 'Jugar otra vez' porque el
            cliente tiene que usar su intento extra. Premio y
            consolation: si el padre pasa onMainMenu, mostramos 2
            opciones (seguir jugando + menú principal). */}
        {isReintento ? (
          <button
            onClick={onNext}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 py-4 text-lg font-bold transition hover:scale-[1.02]"
          >
            <RefreshCw className="h-5 w-5" />
            Jugar otra vez
          </button>
        ) : onMainMenu ? (
          <div className="mt-4 flex w-full flex-col gap-3">
            <button
              onClick={onNext}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-4 text-lg font-bold transition hover:scale-[1.02]"
            >
              <RotateCcw className="h-5 w-5" />
              Seguir jugando
            </button>
            <button
              onClick={onMainMenu}
              className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-white/20 bg-white/5 py-4 text-lg font-bold transition hover:bg-white/10"
            >
              <Home className="h-5 w-5" />
              Menú principal
            </button>
          </div>
        ) : (
          <button
            onClick={onNext}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-4 text-lg font-bold transition hover:scale-[1.02]"
          >
            <RotateCcw className="h-5 w-5" />
            Volver a jugar
          </button>
        )}
      </div>
    </Modal>
  );
}
